import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import type {
  BookingGuest,
  ChatAction,
  ChatReply,
  Salon,
  WidgetEventMessage,
} from '../shared/contracts';
import { ActionCards } from './ActionCards';
import {
  WidgetApi,
  WidgetApiError,
  type MessageOptions,
} from './api';
import { BookingForm } from './BookingForm';
import {
  getWidgetCopy,
  resolveWidgetLanguage,
} from './i18n';
import { CloseIcon, SendIcon, SparkIcon } from './icons';
import {
  clearWidgetState,
  loadWidgetState,
  saveWidgetState,
  type PersistedUiMessage,
  widgetStorageKey,
} from './persistence';
import { turnScrollTop } from './scroll';

export interface WidgetConfig {
  salonSlug: string;
  market: string;
  apiUrl: string;
  locale: string;
  initiallyOpen: boolean;
  placement: 'left' | 'right';
  presentation: 'page' | 'widget';
}

interface UiMessage extends PersistedUiMessage {
  actions: ChatAction[];
}

interface RetryRequest {
  messageId: string;
  message: string;
  options?: MessageOptions;
}

interface UiError {
  message: string;
  code: string;
}

type ScrollIntent =
  | { type: 'bottom' }
  | { type: 'turn'; messageId: string };

function uid() {
  return globalThis.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isSessionAuthorizationError(code: string): boolean {
  return [
    'SESSION_EXPIRED',
    'VISITOR_TOKEN_EXPIRED',
    'VISITOR_TOKEN_INVALID',
    'VISITOR_TOKEN_REQUIRED',
  ].includes(code);
}

export function ChatWidget({ config }: { config: WidgetConfig }) {
  const language = resolveWidgetLanguage(config.locale);
  const copy = getWidgetCopy(language);
  const api = useMemo(
    () => new WidgetApi(config.apiUrl, config.locale),
    [config.apiUrl, config.locale],
  );
  const panelId = useMemo(() => `bc-panel-${uid()}`, []);
  const storageKey = useMemo(
    () => widgetStorageKey(
      config.apiUrl,
      config.salonSlug,
      config.locale,
      config.market,
    ),
    [config.apiUrl, config.locale, config.market, config.salonSlug],
  );
  const restored = useMemo(() => loadWidgetState(storageKey), [storageKey]);
  const [open, setOpen] = useState(config.initiallyOpen);
  const [sessionId, setSessionId] = useState(restored?.sessionId || '');
  const [visitorToken, setVisitorToken] = useState(
    restored?.visitorToken || '',
  );
  const [salon, setSalon] = useState<Salon | null>(restored?.salon || null);
  const [messages, setMessages] = useState<UiMessage[]>(
    restored?.messages || [],
  );
  const [cursor, setCursor] = useState(restored?.cursor || 0);
  const [aiPaused, setAiPaused] = useState(restored?.aiPaused || false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [retryRequest, setRetryRequest] = useState<RetryRequest | null>(null);
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollIntentRef = useRef<ScrollIntent | null>(null);
  const inFlightRef = useRef(false);
  const cursorRef = useRef(restored?.cursor || 0);

  useEffect(() => {
    let cancelled = false;
    inFlightRef.current = true;
    setBusy(true);
    setError(null);
    const startFresh = async () => {
      const session = await api.startSession(
        config.salonSlug,
        config.locale,
        config.market,
      );
      if (cancelled) return;
      cursorRef.current = session.cursor;
      setCursor(session.cursor);
      setAiPaused(session.aiPaused);
      setSessionId(session.sessionId);
      setVisitorToken(session.visitorToken);
      setSalon(session.salon);
      setRetryRequest(null);
      setMessages([
        {
          id: `${session.sessionId}:greeting`,
          role: 'assistant',
          text: session.assistant.message,
          actions: session.assistant.actions,
          source: 'ai',
        },
      ]);
    };
    const initialize = async () => {
      if (!restored || sessionAttempt > 0) {
        await startFresh();
        return;
      }
      try {
        const session = await api.resumeSession(
          restored.sessionId,
          restored.visitorToken,
          config.market,
        );
        if (cancelled) return;
        setSessionId(session.sessionId);
        setSalon(session.salon);
        setAiPaused(session.aiPaused);
        setRetryRequest(null);
      } catch (caught) {
        if (
          caught instanceof WidgetApiError &&
          isSessionAuthorizationError(caught.code)
        ) {
          clearWidgetState(storageKey);
          await startFresh();
          return;
        }
        throw caught;
      }
    };
    initialize()
      .catch((caught) => {
        if (!cancelled) {
          setError({
            message:
              caught instanceof Error
                ? caught.message
                : copy.assistantUnavailable,
            code:
              caught instanceof WidgetApiError
                ? caught.code
                : 'REQUEST_FAILED',
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          inFlightRef.current = false;
          setBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    api,
    config.salonSlug,
    config.locale,
    config.market,
    restored,
    sessionAttempt,
    storageKey,
  ]);

  useEffect(() => {
    if (!sessionId || !visitorToken || !salon || messages.length === 0) return;
    saveWidgetState(storageKey, {
      sessionId,
      visitorToken,
      salon,
      messages,
      cursor,
      aiPaused,
    });
  }, [
    aiPaused,
    cursor,
    messages,
    salon,
    sessionId,
    storageKey,
    visitorToken,
  ]);

  useEffect(() => {
    if (!sessionId || !visitorToken || !open) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const events = await api.getEvents(
          sessionId,
          visitorToken,
          cursorRef.current,
        );
        if (cancelled) return;
        cursorRef.current = Math.max(cursorRef.current, events.cursor);
        setCursor(cursorRef.current);
        setAiPaused(events.aiPaused);
        if (events.messages.length > 0) {
          const firstNewId = events.messages[0]?.id;
          if (firstNewId) {
            scrollIntentRef.current = {
              type: 'turn',
              messageId: firstNewId,
            };
          }
          setMessages((current) => {
            const ids = new Set(current.map((message) => message.id));
            const additions: UiMessage[] = events.messages
              .filter((message) => !ids.has(message.id))
              .map((message) => ({
                id: message.id,
                role: 'assistant',
                text: message.message,
                actions: [],
                source: 'human',
                createdAt: message.createdAt,
              }));
            return additions.length > 0 ? [...current, ...additions] : current;
          });
        }
      } catch (caught) {
        if (
          !cancelled &&
          caught instanceof WidgetApiError &&
          isSessionAuthorizationError(caught.code)
        ) {
          clearWidgetState(storageKey);
          setSessionId('');
          setVisitorToken('');
          setMessages([]);
          setRetryRequest(null);
          setSessionAttempt((current) => current + 1);
          return;
        }
      }
      if (!cancelled) timer = setTimeout(poll, 3_000);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [api, open, sessionId, storageKey, visitorToken]);

  useLayoutEffect(() => {
    if (!open) return;
    const container = scrollRef.current;
    const intent = scrollIntentRef.current;
    if (!container || !intent) return;

    if (intent.type === 'bottom') {
      container.scrollTop = container.scrollHeight;
      scrollIntentRef.current = null;
      return;
    }

    const messageElements = Array.from(
      container.querySelectorAll<HTMLElement>('[data-message-id]'),
    );
    const turnIndex = messageElements.findIndex(
      (element) => element.dataset.messageId === intent.messageId,
    );
    const turn = messageElements[turnIndex];
    if (!turn) return;

    const containerRect = container.getBoundingClientRect();
    const turnRect = turn.getBoundingClientRect();
    const previousUser = messageElements
      .slice(0, turnIndex)
      .reverse()
      .find((element) => element.dataset.messageRole === 'user');
    const previousUserRect = previousUser?.getBoundingClientRect();
    const contentOffset = container.scrollTop - containerRect.top;

    container.scrollTop = turnScrollTop({
      currentScrollTop: container.scrollTop,
      maxScrollTop: Math.max(0, container.scrollHeight - container.clientHeight),
      viewportHeight: container.clientHeight,
      replyTop: turnRect.top + contentOffset,
      replyBottom: turnRect.bottom + contentOffset,
      contextTop: previousUserRect
        ? previousUserRect.top + contentOffset
        : undefined,
    });
    scrollIntentRef.current = null;
  }, [messages, busy, error, open]);

  const appendReply = (reply: ChatReply) => {
    const id = uid();
    scrollIntentRef.current = { type: 'turn', messageId: id };
    setMessages((current) => [
      ...current,
      { id, role: 'assistant', text: reply.message, actions: reply.actions },
    ]);
  };

  const appendHumanEvents = (events: WidgetEventMessage[]) => {
    if (events.length === 0) return;
    const firstNewId = events[0]?.id;
    if (firstNewId) {
      scrollIntentRef.current = { type: 'turn', messageId: firstNewId };
    }
    setMessages((current) => {
      const ids = new Set(current.map((message) => message.id));
      const additions: UiMessage[] = events
        .filter((message) => !ids.has(message.id))
        .map((message) => ({
          id: message.id,
          role: 'assistant',
          text: message.message,
          actions: [],
          source: 'human',
          createdAt: message.createdAt,
        }));
      return additions.length > 0 ? [...current, ...additions] : current;
    });
  };

  const latestActionMessageId = useMemo(
    () =>
      [...messages]
        .reverse()
        .find((message) => message.actions.length > 0)?.id,
    [messages],
  );

  const send = async (
    message: string,
    options?: MessageOptions,
    existingMessageId?: string,
  ) => {
    const clean = message.trim();
    if (!clean || !sessionId || !visitorToken || inFlightRef.current) return;
    inFlightRef.current = true;
    scrollIntentRef.current = { type: 'bottom' };
    const messageId = existingMessageId || uid();
    setMessages((current) =>
      existingMessageId
        ? current.map((item) =>
            item.id === existingMessageId
              ? { ...item, failed: false }
              : item,
          )
        : [
            ...current,
            { id: messageId, role: 'user', text: clean, actions: [] },
          ],
    );
    setInput('');
    setBusy(true);
    setError(null);
    setRetryRequest(null);
    try {
      const response = await api.sendMessage(sessionId, visitorToken, clean, {
        ...options,
        messageId,
      });
      cursorRef.current = Math.max(cursorRef.current, response.cursor);
      setCursor(cursorRef.current);
      setAiPaused(response.aiPaused);
      appendHumanEvents(response.events);
      if (response.delivered) appendReply(response);
    } catch (caught) {
      const requestError = caught instanceof WidgetApiError
        ? caught
        : new WidgetApiError(
            caught instanceof Error
              ? caught.message
              : copy.messageCouldNotBeSent,
          );
      setMessages((current) =>
        current.map((item) =>
          item.id === messageId ? { ...item, failed: true } : item,
        ),
      );
      setRetryRequest({ messageId, message: clean, options });
      setError({ message: requestError.message, code: requestError.code });
      scrollIntentRef.current = { type: 'bottom' };
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  const book = async (draftToken: string, guest: BookingGuest) => {
    if (!sessionId || !visitorToken || inFlightRef.current) {
      throw new Error(copy.assistantStillLoading);
    }
    inFlightRef.current = true;
    scrollIntentRef.current = { type: 'bottom' };
    setBusy(true);
    setError(null);
    try {
      const reply = await api.createBooking(
        sessionId,
        visitorToken,
        draftToken,
        guest,
      );
      appendReply(reply);
      return reply;
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open || config.presentation === 'page') return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [config.presentation, open]);

  return (
    <div
      class={`bc-root bc-${config.placement}${
        config.presentation === 'page' ? ' bc-page' : ''
      }`}
      lang={language}
    >
      {open && (
        <section
          id={panelId}
          class="bc-panel"
          role="dialog"
          aria-label={copy.panelLabel(
            salon?.name || copy.defaultSalonName,
          )}
        >
          <header class="bc-header">
            <div class="bc-brand-mark"><SparkIcon /></div>
            <div class="bc-header-copy">
              <span>{salon?.name || copy.defaultAssistantName}</span>
              <small>
                <i /> {aiPaused ? copy.salonTeamJoined : copy.onlineStatus}
              </small>
            </div>
            {config.presentation === 'widget' && (
              <button
                class="bc-close"
                type="button"
                onClick={() => setOpen(false)}
                aria-label={copy.close}
              >
                <CloseIcon />
              </button>
            )}
          </header>

          <div
            class="bc-messages"
            ref={scrollRef}
            aria-live="polite"
            aria-busy={busy}
          >
            <div class="bc-date-divider"><span>{copy.today}</span></div>
            {messages.map((message) => (
              <div
                class={`bc-message bc-message-${message.role}${
                  message.failed ? ' bc-message-failed' : ''
                }`}
                key={message.id}
                data-message-id={message.id}
                data-message-role={message.role}
              >
                {message.role === 'assistant' && (
                  <span class="bc-message-avatar"><SparkIcon /></span>
                )}
                <div class="bc-message-stack">
                  <div class="bc-message-bubble">
                    {message.text.split('\n').map((line, index) => (
                      <span key={`${message.id}-${index}`}>{line || <br />}</span>
                    ))}
                  </div>
                  {message.actions.map((action, index) =>
                    action.type === 'booking_form' ? (
                      <BookingForm
                        key={`${message.id}-form-${index}`}
                        draft={action.draft}
                        locale={language}
                        disabled={
                          busy ||
                          aiPaused ||
                          (Boolean(latestActionMessageId) &&
                            message.id !== latestActionMessageId)
                        }
                        onSubmit={(guest) => book(action.draft.token, guest)}
                      />
                    ) : (
                      <ActionCards
                        key={`${message.id}-action-${index}`}
                        action={action}
                        locale={language}
                        disabled={
                          busy ||
                          aiPaused ||
                          (Boolean(latestActionMessageId) &&
                            message.id !== latestActionMessageId)
                        }
                        onPrompt={send}
                      />
                    ),
                  )}
                  <small class="bc-message-time">
                    {message.failed ? copy.notSent : copy.justNow}
                  </small>
                </div>
              </div>
            ))}

            {messages.length === 1 && !busy && !aiPaused && (
              <div class="bc-quick-prompts">
                <button
                  type="button"
                  onClick={() =>
                    send(copy.servicesAndPricesPrompt, {
                      intent: 'service_catalogue',
                    })
                  }
                >
                  {copy.servicesAndPrices}
                </button>
                <button
                  type="button"
                  onClick={() => send(copy.findTimePrompt)}
                >
                  {copy.findTime}
                </button>
              </div>
            )}

            {aiPaused && !busy && (
              <div class="bc-handoff-state" role="status">
                {copy.handoffStatus}
              </div>
            )}

            {busy && (
              <div class="bc-message bc-message-assistant">
                <span class="bc-message-avatar"><SparkIcon /></span>
                <div
                  class="bc-typing"
                  role="status"
                  aria-label={copy.assistantReplying}
                >
                  <i /><i /><i />
                </div>
              </div>
            )}
            {error && (
              <div class="bc-error" role="alert">
                <span>{error.message}</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (isSessionAuthorizationError(error.code)) {
                      clearWidgetState(storageKey);
                      setSessionId('');
                      setVisitorToken('');
                      setMessages([]);
                      setRetryRequest(null);
                      setSessionAttempt((current) => current + 1);
                    } else if (retryRequest) {
                      send(
                        retryRequest.message,
                        retryRequest.options,
                        retryRequest.messageId,
                      );
                    } else {
                      setSessionAttempt((current) => current + 1);
                    }
                  }}
                >
                  {isSessionAuthorizationError(error.code)
                    ? copy.restartChat
                    : copy.retry}
                </button>
              </div>
            )}
          </div>

          <footer class="bc-footer">
            <form
              class="bc-composer"
              onSubmit={(event) => {
                event.preventDefault();
                send(input);
              }}
            >
              <textarea
                rows={1}
                value={input}
                onInput={(event) => setInput(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    send(input);
                  }
                }}
                disabled={!sessionId || !visitorToken || busy}
                maxlength={2000}
                placeholder={copy.composerPlaceholder}
                aria-label={copy.message}
              />
              <button
                type="submit"
                disabled={!sessionId || !visitorToken || !input.trim() || busy}
                aria-label={copy.send}
              >
                <SendIcon />
              </button>
            </form>
            <div class="bc-footer-meta">
              <span>{copy.madeBy} <strong>BLINKIT</strong></span>
            </div>
          </footer>
        </section>
      )}

      {config.presentation === 'widget' && (
        <button
          class={`bc-launcher ${open ? 'bc-launcher-open' : ''}`}
          type="button"
          onClick={() => setOpen((current) => !current)}
          aria-label={open ? copy.closeAssistant : copy.openAssistant}
          aria-controls={panelId}
          aria-expanded={open}
        >
          {open ? <CloseIcon /> : <SparkIcon />}
          {!open && <span>{copy.askAndBook}</span>}
        </button>
      )}
    </div>
  );
}
